import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Search, YouTube } from '@mui/icons-material';
import { ArrowUpRight, Info, Plus, Store } from 'lucide-react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import InstagramIcon from '../../../components/icons/instagram-icon';
import InstagramIcon1 from '../../../components/icons/instagram-icon1';
import { BrandDataService } from '../../../core/services/brand-data.service';
import { RolesService } from '../../../core/services/roles.service';
import { LocalService } from '../../../core/services';
import { Loading } from '../../../redux/action';
import * as gtUtil from '../../../core/utility';
import { Tooltip as MuiTooltip } from '@mui/material';
import { DefaultUserProfile } from '../../../config/config';
import AddBrandPopup from './add-brand-popup';
import '../../../modules/shared/theme/theme.scss';
import './brand-pulse-search.scss';


interface Props {
  UserReducer: any;
  loading: (data: boolean) => void;
}

interface Influencer {
  industry_name: string;
  username: string;
  name: string;
  followers: number;
  gcp_profile_image_url: string;
}

const brandDataService = new BrandDataService();
const rolesService = new RolesService();
const localService = new LocalService();
const methods = new gtUtil.Methods()

const BrandPulseSearchbar: React.FC<Props> = ({ UserReducer, loading }) => {
  const [userStatus, setUserStatus] = useState(false);
  const [searchList, setSearchList] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [isSelected, setIsSelected] = useState(false);
  const [data, setData] = useState<Influencer[]>([]);
  const [groupedData, setGroupedData] = useState<Record<string, Influencer[]>>({});
  const [openAddBrand, setOpenAddBrand] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  /** ─────────────────────────────
   *  ✅ Access Control
   *  ───────────────────────────── */
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await rolesService.checkAccess({
          access_roles: ['brand_pulse'],
          user_id: UserReducer?.user_id,
        });
        if (res?.data?.rows?.length > 0) {
          setUserStatus(true);
        } else {
          localService.toastify('You are not allowed to access', 'error', 1000);
          navigate('/');
        }
      } catch (err) {
        console.error('Access check failed:', err);
      }
    };



    checkAccess();
  }, [UserReducer?.user_id, navigate]);

  /** ─────────────────────────────
   *  ✅ Click outside to close dropdown
   *  ───────────────────────────── */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsSelected(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (userStatus) {
      getAllBrandData();
    }
  }, [userStatus])


  const getAllBrandData = async () => {
    try {
      const res = await brandDataService.getBrandCategorySearchBar();

      const rows: Influencer[] = res?.data ?? [];
      if (rows.length > 0) {
        const grouped = rows.reduce((acc: Record<string, Influencer[]>, item) => {
          const cleanIndustry = item.industry_name?.trim();
          if (!acc[cleanIndustry]) acc[cleanIndustry] = [];
          acc[cleanIndustry].push(item);
          return acc;
        }, {});
        const sortedGrouped = Object.keys(grouped)
          .sort((a, b) => a.localeCompare(b))
          .reduce((acc, key) => {
            acc[key] = grouped[key];
            return acc;
          }, {} as Record<string, any[]>);
        setGroupedData(sortedGrouped);
      }
    } catch (err) {
      console.error("Access check failed:", err);
    }
  };
  /** ─────────────────────────────
   *  ✅ Search Handler
   *  ───────────────────────────── */
  const handleSearch = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.toLowerCase();
    setSearchValue(input);

    if (input.length < 3) {
      setSearchList([]);
      return;
    }

    try {
      const response = await brandDataService.getBrandSearch(input);
      if (response.status === gtUtil.Constants.SUCCESS) {
        setSearchList(response.data.rows || []);
      } else {
        setSearchList([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setSearchList([]);
    }
  }, []);

  /** ─────────────────────────────
   *  ✅ On Selecting an Item
   *  ───────────────────────────── */
  const handleSelectItem = useCallback(
    (item: any) => {
      const username = item?.username;
      const ref_id = item?.ref_id;
      setSearchValue(username);
      setIsSelected(false);

      // Navigate to details page with params
      navigate(`${location.pathname}/${ref_id}/${username}`);
    },
    [navigate]
  );

  /** ─────────────────────────────
   *  ✅ Render a Single Search Item
   *  ───────────────────────────── */
  const renderProfileListItem = (item: any) => {
    const displayName = item?.platform === 'instagram' ? item?.username : item?.title;
    const platformName = item?.platform === 'instagram' ? item?.insta_name : item?.title;

    return (
      <li key={item?.ref_id}>
        <button type="button" className="brd-pulse-suggest__item" onClick={() => handleSelectItem(item)}>
          <span className="brd-pulse-suggest__icon">
            {item?.platform === 'instagram'
              ? <InstagramIcon1 width={20} />
              : <YouTube fontSize="small" style={{ color: 'red' }} />}
          </span>
          <span className="brd-pulse-suggest__text">
            <span className="brd-pulse-suggest__name">{displayName}</span>
            {!!platformName && <span className="brd-pulse-suggest__sub">{platformName}</span>}
          </span>
          <ArrowUpRight size={16} className="brd-pulse-suggest__go" />
        </button>
      </li>
    );
  };

  /** ─────────────────────────────
   *  ✅ Main Render
   *  ───────────────────────────── */
  if (!userStatus) return null;

  const industries = Object.entries(groupedData);
  const brandCount = industries.reduce((n, [, list]) => n + list.length, 0);

  return (
    <div className="brd brd-pulse">
      {/* White top block, like Campaigns: title + Add Brand, then the search */}
      <div className="brd-pulse-top">
        <div className="brd-container">
          <header className="brd-pulse-top__head">
            <div className="brd-pulse-top__heading">
              <h1 className="brd-pulse-top__title">Brand Pulse</h1>
              <p className="brd-pulse-top__sub">Look up any brand's Instagram — its posts, engagement, and what people say about it.</p>
            </div>
            {/* Adds the handle to sheet_brand_handles_data so the profile-fetch service starts tracking it */}
            <div className="brd-pulse-top__actions">
              <MuiTooltip
                arrow
                placement='bottom'
                title="Once you add a brand, the Brand Pulse service picks it up on its own. The Instagram profile and posts are pulled within about 30 minutes, and post analytics plus comment sentiment run on the next scheduled pass (2nd & 16th of every month). After that the brand keeps refreshing every 15 days."
              >
                <span className="brd-icon-btn" aria-label="How adding a brand works"><Info size={18} /></span>
              </MuiTooltip>
              <button type="button" className="brd-btn brd-btn--primary" onClick={() => setOpenAddBrand(true)}>
                <Plus size={16} /> Add Brand
              </button>
            </div>
          </header>

          <div className="brd-pulse-search" ref={searchRef}>
            <form onSubmit={(e) => e.preventDefault()}>
              <label className="brd-search brd-pulse-search__box">
                <InstagramIcon width={18} height={18} />
                <input
                  type="search"
                  placeholder="Search a brand by name or username"
                  id="brand-search-by-name"
                  aria-label="Search a brand by name or username"
                  value={searchValue}
                  onChange={handleSearch}
                  onClick={() => setIsSelected(true)}
                  autoComplete="off"
                />
                <Search fontSize="small" />
              </label>
            </form>
            {isSelected && searchValue.length > 0 && searchValue.length < 3 && (
              <p className="brd-pulse-search__hint">Type at least 3 letters to search.</p>
            )}
            {isSelected && searchList.length > 0 && (
              <ul className="brd-pulse-suggest" role="listbox">
                {searchList.slice(0, 10).map(renderProfileListItem)}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="brd-container brd-pulse__body">
        <div className="brd-pulse__section-head">
          <h2 className="brd-pulse__section-title">Brands by industry</h2>
          {brandCount > 0 && <span className="brd-pulse__section-count">{brandCount} brands · {industries.length} industries</span>}
        </div>

        {industries.length === 0 ? (
          <div className="brd-card brd-empty">
            <span className="brd-empty__icon"><Store size={28} /></span>
            <p className="brd-empty__title">No brands yet</p>
            <p className="brd-empty__body">Add a brand to start tracking it, or search for one above.</p>
          </div>
        ) : industries.map(([industry, influencers]) => (
          <section className="brd-pulse-industry" key={industry}>
            <h3 className="brd-pulse-industry__title">
              {industry || 'Other'} <span className="brd-pill brd-pill--muted">{influencers.length}</span>
            </h3>
            <div className="brd-pulse-grid">
              {influencers.map((inf: any) => (
                <button type="button" className="brd-card brd-pulse-brand" key={inf.username} onClick={() => handleSelectItem(inf)}>
                  <img
                    src={inf.gcp_profile_image_url || DefaultUserProfile.influencer_unknown_default}
                    alt=""
                    className="brd-pulse-brand__avatar"
                  />
                  <span className="brd-pulse-brand__text">
                    <span className="brd-pulse-brand__name" title={inf.name}>{inf.name}</span>
                    <span className="brd-pulse-brand__user" title={inf.username}>@{inf.username}</span>
                  </span>
                  <span className="brd-pulse-brand__followers" title={`${methods.convertNumber(inf.followers)} followers`}>
                    <strong>{methods.convertNumber(inf.followers)}</strong>
                    <span>followers</span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      {openAddBrand && (
        <AddBrandPopup
          openModal={openAddBrand}
          user_id={UserReducer?.user_id}
          onClose={(added: boolean) => {
            setOpenAddBrand(false);
            // a newly added brand only shows up here after the service fetches its profile,
            // but refresh anyway so nothing added earlier is missed
            if (added) getAllBrandData();
          }}
        />
      )}
    </div>
  );
};

/** ─────────────────────────────
 *  ✅ Redux Connection
 *  ───────────────────────────── */
const mapStateToProps = (state: any) => ({
  UserReducer: state.UserReducer.UserData,
});

const mapDispatchToProps = (dispatch: any) => ({
  loading: (data: boolean) => dispatch(Loading(data)),
});

const connectToRedux = connect(
  mapStateToProps,
  mapDispatchToProps
)(BrandPulseSearchbar);

export { connectToRedux as BrandPulseSearchbar };
